import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Crown, IndianRupee, Wallet as WalletIcon, ShoppingBag,
  TrendingUp, ArrowUpFromLine, ArrowDownToLine, Search,
} from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const KIND_STYLE = {
  customer_payment:     { color: "bg-brand-green/10 text-brand-green border-brand-green/20",   label: "Customer payment" },
  admin_commission:     { color: "bg-brand-blue/10 text-brand-blue border-brand-blue/20",       label: "Admin commission" },
  wallet_credit:        { color: "bg-brand-green/10 text-brand-green border-brand-green/20",   label: "Wallet credit" },
  wallet_debit:         { color: "bg-fuchsia-500/10 text-fuchsia-600 border-fuchsia-500/20",   label: "Wallet debit" },
  withdrawal:           { color: "bg-amber-500/10 text-amber-600 border-amber-500/20",         label: "Withdrawal" },
  subscription_payment: { color: "bg-primary/10 text-primary border-primary/20",               label: "Subscription payment" },
};

const Stat = ({ label, value, icon: Icon, tint = "brand-blue" }) => (
  <Card className="p-4 border-border bg-card">
    <div className="flex items-start gap-3">
      <div className={`h-9 w-9 rounded-lg bg-${tint}/10 text-${tint} flex items-center justify-center`}>
        <Icon size={16} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="text-lg font-display font-semibold mt-0.5">{value}</p>
      </div>
    </div>
  </Card>
);

export default function AdminRetailerDetail() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [tx, setTx] = useState(null);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([
        api.get(`/admin/retailers/${id}`),
        api.get(`/admin/retailers/${id}/transactions`),
      ]);
      setDetail(a.data);
      setTx(b.data);
    })();
  }, [id]);

  if (!detail || !tx) {
    return <div className="p-6 text-sm text-muted-foreground">Loading retailer detail…</div>;
  }

  const p = detail.profile;
  const sub = detail.subscription;
  const events = (tx.transactions || []).filter((e) => {
    if (kindFilter !== "all" && e.kind !== kindFilter) return false;
    if (q.trim() && !(e.description || "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-retailer-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/retailers" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft size={12} /> Back to retailers
          </Link>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-2">{p.name || "Retailer"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{p.email} · {p.phone}</p>
          {p.shop_name && (
            <p className="text-sm mt-1">
              Shop: <span className="font-medium">{p.shop_name}</span>
              {p.shop_slug && <span className="ml-2 font-mono text-[11px] text-muted-foreground">/{p.shop_slug}</span>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {sub.is_active ? (
            <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/20">
              <Crown size={12} className="mr-1" /> Subscription active
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-muted">Subscription inactive</Badge>
          )}
          {p.approved ? (
            <Badge variant="outline" className="bg-brand-blue/10 text-brand-blue border-brand-blue/20">Approved</Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Pending approval</Badge>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Wallet balance" value={money(detail.wallet_balance)} icon={WalletIcon} tint="brand-blue" />
        <Stat label="Orders" value={detail.orders} icon={ShoppingBag} tint="fuchsia-500" />
        <Stat label="Revenue" value={money(detail.revenue)} icon={IndianRupee} tint="brand-green" />
        <Stat label="Commission earned" value={money(detail.commission_earned)} icon={TrendingUp} tint="brand-blue" />
        <Stat label="Withdrawn (paid)" value={money(detail.withdrawn)} icon={ArrowUpFromLine} tint="fuchsia-500" />
        <Stat label="Pending withdrawals" value={`${detail.pending_withdrawal_count} · ${money(detail.pending_withdrawal_amount)}`} icon={ArrowDownToLine} tint="amber-500" />
        <Stat label="Pages printed" value={detail.prints} icon={ShoppingBag} tint="brand-blue" />
        <Stat label={sub.is_active ? "Subscription plan" : (sub.plan ? "Last plan" : "Subscription")} value={sub.plan || "—"} icon={Crown} tint={sub.is_active ? "brand-green" : "muted-foreground"} />
      </div>

      {/* Subscription meta */}
      {sub.starts_at && (
        <Card className="p-4 border-border bg-card">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Subscription</p>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-muted-foreground text-xs">Plan</span><br/><b>{sub.plan}</b></div>
            <div><span className="text-muted-foreground text-xs">Status</span><br/><b>{sub.status}</b></div>
            <div><span className="text-muted-foreground text-xs">Starts</span><br/><b>{fmtDate(sub.starts_at)}</b></div>
            <div><span className="text-muted-foreground text-xs">Expires</span><br/><b>{fmtDate(sub.expires_at)}</b></div>
          </div>
        </Card>
      )}

      {/* Transactions */}
      <Card className="p-5 border-border bg-card" data-testid="retailer-tx-card">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold">Transaction history</h3>
            <p className="text-xs text-muted-foreground">
              Everything this retailer touched — customer payments, admin commission, wallet credits &amp; debits, withdrawals, subscription payments.
            </p>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search description…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-[220px]"
              data-testid="retailer-tx-search"
            />
          </div>
        </div>
        <Tabs value={kindFilter} onValueChange={setKindFilter} className="mb-3">
          <TabsList data-testid="retailer-tx-filter">
            <TabsTrigger value="all">All ({tx.transactions.length})</TabsTrigger>
            <TabsTrigger value="customer_payment">Customer payments</TabsTrigger>
            <TabsTrigger value="admin_commission">Admin commission</TabsTrigger>
            <TabsTrigger value="wallet_credit">Wallet credits</TabsTrigger>
            <TabsTrigger value="wallet_debit">Wallet debits</TabsTrigger>
            <TabsTrigger value="withdrawal">Withdrawals</TabsTrigger>
            <TabsTrigger value="subscription_payment">Subscriptions</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="rounded-lg border overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e, i) => {
                const style = KIND_STYLE[e.kind] || { color: "bg-muted", label: e.kind };
                return (
                  <TableRow key={`${e.ref_id}-${i}`}>
                    <TableCell className="font-mono text-[11px]">{fmtDate(e.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={style.color}>{style.label}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{e.description}</TableCell>
                    <TableCell><Badge variant="outline">{e.status}</Badge></TableCell>
                    <TableCell className={`text-right font-medium ${e.type === "credit" ? "text-brand-green" : "text-fuchsia-600"}`}>
                      {e.type === "credit" ? "+" : "−"}{money(e.amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-sm text-muted-foreground">
                    No transactions in this view.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
